import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { db, app } from "../../lib/firebase";
import { collection, query, where, getDocs, deleteDoc, doc, updateDoc, getDoc, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BookOpen, PlusCircle, Trash2, Edit, ArrowLeft, ChevronUp, ChevronDown, Lightbulb, CheckCircle, AlertCircle, Loader2, Video } from "lucide-react";

export default function LessonList() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lessons, setLessons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [videoJobs, setVideoJobs] = useState<{[key: string]: any}>({});
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [processingCount, setProcessingCount] = useState(0);

  // Function to fetch video job status
  const fetchVideoJob = async (jobId: string) => {
    try {
      const jobDoc = await getDoc(doc(db, 'video_jobs', jobId));
      if (jobDoc.exists()) {
        const jobData = jobDoc.data();
        setVideoJobs(prev => ({ ...prev, [jobId]: jobData }));
        return jobData;
      }
    } catch (error) {
      console.error(`Error fetching video job ${jobId}:`, error);
    }
    return null;
  };

  // Function to fetch all video jobs for lessons
  const fetchVideoJobs = async () => {
    const jobIds = lessons
      .filter(lesson => lesson.videoJobId)
      .map(lesson => lesson.videoJobId);
    
    const promises = jobIds.map(jobId => fetchVideoJob(jobId));
    const results = await Promise.all(promises);
    
    // Count processing videos
    const processing = results.filter(job => 
      job && (job.status === 'processing' || job.status === 'uploaded')
    ).length;
    setProcessingCount(processing);
  };

  useEffect(() => {
    async function load() {
      if (!id) return;
      try {
        const q = query(
          collection(db, "lessons"), 
          where("courseId", "==", id)
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort by orderIndex in JavaScript
        data.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
        setLessons(data);
        
        // Fetch video jobs after loading lessons
        await fetchVideoJobs();
      } catch (error) {
        console.error("Error loading lessons:", error);
      }
      setLoading(false);
    }
    load();
  }, [id]);

  // Set up real-time listeners for processing videos
  useEffect(() => {
    if (lessons.length === 0) return;

    const jobIds = lessons
      .filter(lesson => lesson.videoJobId)
      .map(lesson => lesson.videoJobId);

    if (jobIds.length === 0) return;

    // Set up real-time listeners for each video job
    const unsubscribes = jobIds.map(jobId => {
      return onSnapshot(doc(db, 'video_jobs', jobId), (snapshot) => {
        if (snapshot.exists()) {
          const jobData = snapshot.data();
          setVideoJobs(prev => ({ ...prev, [jobId]: jobData }));
          
          // Count processing videos
          const processing = Object.values({ ...videoJobs, [jobId]: jobData }).filter(job => 
            job && (job.status === 'processing' || job.status === 'uploaded')
          ).length;
          setProcessingCount(processing);
        }
      });
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [lessons.map(l => l.videoJobId).join(',')]);

  const remove = async (lessonId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ว่าต้องการลบบทเรียนนี้?\n\nการลบจะคืนพื้นที่ storage ให้คุณโดยอัตโนมัติ")) return;
    
    setDeleting(lessonId);
    try {
      const functions = getFunctions(app, 'us-central1');
      const deleteLessonFn = httpsCallable(functions, 'deleteLesson');
      
      console.log('[LessonList] Calling deleteLesson function for:', lessonId);
      const result = await deleteLessonFn({ lessonId });
      const data = result.data as any;
      
      console.log('[LessonList] Delete result:', data);
      
      if (data && data.success) {
        setLessons((prev) => prev.filter((l) => l.id !== lessonId));
        
        // Show success message with storage freed info
        const storageMB = (data.storageFreed / (1024 * 1024)).toFixed(2);
        const successMsg = document.createElement('div');
        successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-xl z-50 animate-fadeIn';
        successMsg.innerHTML = `
          <div class="font-semibold">✓ ลบบทเรียนสำเร็จ!</div>
          ${data.storageFreed > 0 ? `<div class="text-sm mt-1">คืนพื้นที่: ${storageMB} MB</div>` : ''}
        `;
        document.body.appendChild(successMsg);
        
        // Reload page after 2 seconds to refresh storage quota
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        console.error('[LessonList] Delete failed, no success flag');
        alert('เกิดข้อผิดพลาด: ไม่สามารถลบบทเรียนได้');
      }
    } catch (error: any) {
      console.error("[LessonList] Error deleting lesson:", error);
      console.error("[LessonList] Error details:", error.code, error.message, error.details);
      alert(`เกิดข้อผิดพลาดในการลบบทเรียน: ${error.message}`);
    }
    setDeleting(null);
  };

  const moveLesson = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === lessons.length - 1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const newLessons = [...lessons];
    const temp = newLessons[index];
    newLessons[index] = newLessons[newIndex];
    newLessons[newIndex] = temp;

    // Update orderIndex for both lessons
    try {
      await updateDoc(doc(db, "lessons", newLessons[index].id), {
        orderIndex: index + 1
      });
      await updateDoc(doc(db, "lessons", newLessons[newIndex].id), {
        orderIndex: newIndex + 1
      });

      // Update local state
      newLessons[index].orderIndex = index + 1;
      newLessons[newIndex].orderIndex = newIndex + 1;
      setLessons(newLessons);

      // Show success message
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-xl z-50 animate-fadeIn';
      successMsg.textContent = '✓ เปลี่ยนตำแหน่งสำเร็จ!';
      document.body.appendChild(successMsg);
      setTimeout(() => successMsg.remove(), 2000);
    } catch (error) {
      console.error("Error moving lesson:", error);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนตำแหน่ง");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดบทเรียน...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-4 md:py-8 px-3 md:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <button
            onClick={() => nav(`/instructor/courses/${id}/edit`)}
            className="flex items-center gap-1.5 md:gap-2 text-textLight hover:text-textDark text-sm md:text-base transition-colors mb-3 md:mb-4"
          >
            <ArrowLeft className="w-4 md:w-5 h-4 md:h-5" />
            กลับไปแก้ไขคอร์ส
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-textDark mb-1 md:mb-2">จัดการบทเรียน</h1>
              <p className="text-textLight text-sm md:text-lg">
                {lessons.length} บทเรียน
              </p>
            </div>
            
            <Link
              to={`/instructor/courses/${id}/lessons/new`}
              className="flex items-center gap-1.5 md:gap-2 bg-gradient-primary text-white px-3 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-xs md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <PlusCircle className="w-4 md:w-5 h-4 md:h-5" />
              <span className="hidden sm:inline">เพิ่มบทเรียนใหม่</span>
              <span className="sm:hidden">เพิ่ม</span>
            </Link>
          </div>
        </div>

        {/* Processing Videos Banner */}
        {processingCount > 0 && (
          <div className="mb-4 md:mb-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl md:rounded-2xl shadow-xl p-4 md:p-6 text-white animate-pulse">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="bg-white/20 p-2 md:p-3 rounded-lg md:rounded-xl">
                <Loader2 className="w-6 md:w-8 h-6 md:h-8 animate-spin" />
              </div>
              <div className="flex-1">
                <h3 className="text-base md:text-xl font-bold mb-0.5 md:mb-1">กำลังประมวลผลวิดีโอ</h3>
                <p className="text-xs md:text-sm text-blue-100">
                  มี {processingCount} วิดีโอที่กำลังประมวลผลในเบื้องหลัง - คุณสามารถทำงานอื่นต่อได้เลย
                </p>
              </div>
              <div className="bg-white/20 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl">
                <p className="text-xl md:text-2xl font-bold">{processingCount}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {lessons.length === 0 && (
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-8 md:p-12 text-center">
            <div className="bg-blue-100 w-20 md:w-24 h-20 md:h-24 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
              <BookOpen className="w-10 md:w-12 h-10 md:h-12 text-blue-600" />
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-textDark mb-2 md:mb-3">ยังไม่มีบทเรียน</h2>
            <p className="text-sm md:text-base text-textLight mb-4 md:mb-6 max-w-md mx-auto">
              เริ่มสร้างบทเรียนแรกของคุณเพื่อให้นักเรียนสามารถเข้าถึงเนื้อหาได้
            </p>
            <Link
              to={`/instructor/courses/${id}/lessons/new`}
              className="inline-flex items-center gap-2 bg-gradient-primary text-white px-6 md:px-8 py-3 md:py-4 rounded-lg md:rounded-xl text-sm md:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              <PlusCircle className="w-5 md:w-6 h-5 md:h-6" />
              สร้างบทเรียนแรก
            </Link>
          </div>
        )}

        {/* Lessons List */}
        {lessons.length > 0 && (
          <div className="space-y-3 md:space-y-4">
            {lessons.map((lesson, index) => (
              <div
                key={lesson.id}
                className="bg-white rounded-xl md:rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="flex items-center gap-2 md:gap-4 p-3 md:p-6">
                  {/* Order Number */}
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-primary text-white w-10 md:w-12 h-10 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center font-bold text-base md:text-lg shadow-md">
                      {lesson.orderIndex}
                    </div>
                  </div>

                  {/* Thumbnail */}
                  <div className="flex-shrink-0 relative hidden sm:block">
                    <div className="w-24 md:w-32 h-16 md:h-20 rounded-lg md:rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center shadow-md">
                      <Video className="w-6 md:w-8 h-6 md:h-8 text-blue-400" />
                    </div>
                    <div className="absolute inset-0 bg-black/20 rounded-lg md:rounded-xl flex items-center justify-center">
                      <div className="bg-white/90 rounded-full p-1.5 md:p-2">
                        <span className="text-[10px] md:text-xs font-semibold text-gray-700">#{lesson.orderIndex}</span>
                      </div>
                    </div>
                    {/* Video upload status indicator */}
                    <div className="absolute top-1 md:top-2 right-1 md:right-2">
                      {lesson.videoJobId && videoJobs[lesson.videoJobId] ? (
                        (() => {
                          const job = videoJobs[lesson.videoJobId];
                          if (job.status === 'ready') {
                            return (
                              <div className="bg-green-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5" title="วิดีโอพร้อมให้นักเรียนเรียนแล้ว">
                                <CheckCircle className="w-5 h-5" />
                                <span className="text-sm font-bold pr-1.5">100%</span>
                              </div>
                            );
                          } else if (job.status === 'processing' || job.status === 'uploaded') {
                            const progress = job.progress || (job.status === 'uploaded' ? 5 : 10);
                            return (
                              <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5 animate-pulse" title={`กำลังประมวลผลวิดีโอ ${job.currentStep || ''}`}>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span className="text-sm font-bold pr-1.5">
                                  {Math.round(progress)}%
                                </span>
                              </div>
                            );
                          } else if (job.status === 'failed') {
                            return (
                              <div className="bg-red-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5" title="เกิดข้อผิดพลาดในการประมวลผล">
                                <AlertCircle className="w-5 h-5" />
                                <span className="text-sm font-bold pr-1.5">ERROR</span>
                              </div>
                            );
                          } else {
                            return (
                              <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5 animate-pulse" title="กำลังเตรียมวิดีโอ">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span className="text-sm font-bold pr-1.5">5%</span>
                              </div>
                            );
                          }
                        })()
                      ) : lesson.videoJobId ? (
                        // Has jobId but no job status yet - show pending
                        <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5 animate-pulse" title="กำลังเตรียมวิดีโอ">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm font-bold pr-1.5">5%</span>
                        </div>
                      ) : lesson.videoUrl ? (
                        <div className="bg-green-500 text-white rounded-full p-2 shadow-lg flex items-center gap-1.5" title="วิดีโอพร้อมใช้งาน">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-bold pr-1.5">100%</span>
                        </div>
                      ) : (
                        <div className="bg-blue-500 text-white rounded-full p-2 shadow-lg" title="ยังไม่มีวิดีโอ">
                          <AlertCircle className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm md:text-xl font-bold text-textDark mb-0.5 md:mb-1 truncate">
                      {lesson.title}
                    </h3>
                    <p className="text-xs md:text-sm text-textLight line-clamp-1 md:line-clamp-2">
                      {lesson.description || "ไม่มีคำอธิบาย"}
                    </p>
                    {/* Processing status text */}
                    {lesson.videoJobId && videoJobs[lesson.videoJobId] && 
                     (videoJobs[lesson.videoJobId].status === 'processing' || 
                      videoJobs[lesson.videoJobId].status === 'uploaded') && (
                      <div className="mt-2 flex items-center gap-2 text-blue-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-xs font-semibold">
                          กำลังประมวลผลวิดีโอในเบื้องหลัง...
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 md:gap-2">
                    {/* Move Up Button */}
                    <button
                      onClick={() => moveLesson(index, 'up')}
                      disabled={index === 0}
                      className="p-2 md:p-3 rounded-lg md:rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="เลื่อนขึ้น"
                    >
                      <ChevronUp className="w-4 md:w-5 h-4 md:h-5" />
                    </button>
                    
                    {/* Move Down Button */}
                    <button
                      onClick={() => moveLesson(index, 'down')}
                      disabled={index === lessons.length - 1}
                      className="p-2 md:p-3 rounded-lg md:rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="เลื่อนลง"
                    >
                      <ChevronDown className="w-4 md:w-5 h-4 md:h-5" />
                    </button>

                    {/* Edit Button */}
                    <Link
                      to={`/instructor/courses/${id}/lessons/${lesson.id}/edit`}
                      className="p-2 md:p-3 rounded-lg md:rounded-xl bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                      title="แก้ไขบทเรียน"
                    >
                      <Edit className="w-4 md:w-5 h-4 md:h-5" />
                    </Link>
                    
                    {/* Delete Button */}
                    <button
                      onClick={() => remove(lesson.id)}
                      disabled={deleting === lesson.id}
                      className="p-2 md:p-3 rounded-lg md:rounded-xl bg-red-50 text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50"
                      title="ลบบทเรียน"
                    >
                      {deleting === lesson.id ? (
                        <div className="animate-spin rounded-full h-4 md:h-5 w-4 md:w-5 border-b-2 border-red-600"></div>
                      ) : (
                        <Trash2 className="w-4 md:w-5 h-4 md:h-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips Section */}
        {lessons.length > 0 && (
          <div className="mt-8 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 border-2 border-blue-100">
            <h3 className="text-2xl font-bold text-textDark mb-4 flex items-center gap-3">
              <Lightbulb className="w-8 h-8 text-yellow-500" />
              เคล็ดลับการจัดการบทเรียน
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-bold text-textDark mb-2">1. จัดเรียงตามลำดับ</h4>
                <p className="text-sm text-textLight">
                  บทเรียนจะแสดงตามลำดับที่กำหนด ควรเรียงจากง่ายไปยาก
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-bold text-textDark mb-2">2. ใช้ชื่อที่ชัดเจน</h4>
                <p className="text-sm text-textLight">
                  ตั้งชื่อบทเรียนให้สื่อความหมายและเข้าใจง่าย
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-bold text-textDark mb-2">3. เพิ่มคำอธิบาย</h4>
                <p className="text-sm text-textLight">
                  คำอธิบายช่วยให้นักเรียนรู้ว่าจะได้เรียนอะไรในบทนี้
                </p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <h4 className="font-bold text-textDark mb-2">4. เคล็ดลับการอัปโหลด</h4>
                <p className="text-sm text-textLight">
                  ใช้ไฟล์ .mp4, .mov หรือ .avi เพื่อความเร็วในการประมวลผล - วิดีโอจะพร้อมใช้งานภายใน 2-3 นาที
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
